package com.checkin.host.ui.reservations

import androidx.compose.runtime.Composable
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.cardStyle
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.shared.dto.VerificationConfigDto
import org.jetbrains.compose.web.attributes.ref
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLTextAreaElement

@Composable
fun VerificationConfigPanel(
    config: VerificationConfigDto,
    onDocumentEnabledChange: (Boolean) -> Unit,
    onSelfieEnabledChange: (Boolean) -> Unit,
    onContractEnabledChange: (Boolean) -> Unit,
    onCustomMessageChange: (String) -> Unit,
) {
    val isMobile = rememberIsMobile()

    H3({
        style {
            margin(0.px, 0.px, 4.px, 0.px)
            fontSize(18.px)
        }
    }) { Text("Configuration de vérification") }
    P({
        style {
            color(AppColors.muted)
            fontSize(14.px)
            margin(0.px, 0.px, 16.px, 0.px)
        }
    }) {
        Text("Choisissez les étapes incluses dans le lien check-in.")
    }

    VerificationStepCard(
        number = 1,
        title = "Vérification de Document",
        subtitle = "Scan de la pièce d'identité",
        iconBackground = Color("#ede9fe"),
        iconEmoji = "🪪",
        enabled = config.documentEnabled,
        onEnabledChange = onDocumentEnabledChange,
        activateReasons = listOf(
            "Sécurité maximale",
            "Conformité légale",
            "Protection contre la fraude",
        ),
        deactivateReasons = listOf(
            "Check-in plus rapide",
            "Moins de friction",
            "Idéal pour séjours courts",
        ),
        stackReasons = isMobile,
    )

    VerificationStepCard(
        number = 2,
        title = "Vérification par Selfie",
        subtitle = "Photo et mouvements de sécurité",
        iconBackground = Color("rgba(99,102,241,0.15)"),
        iconEmoji = "📷",
        enabled = config.selfieEnabled,
        onEnabledChange = onSelfieEnabledChange,
        activateReasons = listOf(
            "Sécurité renforcée",
            "Vérification d'identité",
            "Réduction des risques",
        ),
        deactivateReasons = listOf(
            "Évite les problèmes techniques",
            "Meilleure expérience utilisateur",
            "Taux de réussite +15-20 %",
        ),
        stackReasons = isMobile,
    )

    VerificationStepCard(
        number = 3,
        title = "Signature de Contrat",
        subtitle = "Signature électronique du contrat",
        iconBackground = Color("#dbeafe"),
        iconEmoji = "📝",
        enabled = config.contractEnabled,
        onEnabledChange = onContractEnabledChange,
        activateReasons = listOf(
            "Protection légale",
            "Preuve de séjour",
            "Conformité réglementaire",
        ),
        deactivateReasons = listOf(
            "Check-in plus rapide",
            "Moins d'étapes",
            "Idéal pour séjours courts",
        ),
        stackReasons = isMobile,
    )

    CustomMessageSection(
        message = config.customMessage.orEmpty(),
        onMessageChange = onCustomMessageChange,
    )
}

@Composable
private fun VerificationStepCard(
    number: Int,
    title: String,
    subtitle: String,
    iconBackground: CSSColorValue,
    iconEmoji: String,
    enabled: Boolean,
    onEnabledChange: (Boolean) -> Unit,
    activateReasons: List<String>,
    deactivateReasons: List<String>,
    stackReasons: Boolean = false,
) {
    Div({
        style {
            cardStyle()
            marginBottom(12.px)
            property("box-shadow", "none")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                gap(12.px)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    alignItems(AlignItems.FlexStart)
                    flex(1)
                }
            }) {
                Div({
                    style {
                        width(40.px)
                        height(40.px)
                        borderRadius(10.px)
                        backgroundColor(iconBackground)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        fontSize(20.px)
                        property("flex-shrink", "0")
                    }
                }) { Text(iconEmoji) }
                Div {
                    H4({
                        style {
                            margin(0.px)
                            fontSize(16.px)
                            color(AppColors.text)
                        }
                    }) { Text("$number. $title") }
                    P({
                        style {
                            margin(4.px, 0.px, 0.px, 0.px)
                            fontSize(13.px)
                            color(AppColors.muted)
                        }
                    }) { Text(subtitle) }
                }
            }
            ToggleSwitch(checked = enabled, onChange = onEnabledChange)
        }

        Div({
            style {
                display(if (stackReasons) DisplayStyle.Flex else DisplayStyle.Grid)
                if (stackReasons) {
                    flexDirection(FlexDirection.Column)
                } else {
                    property("grid-template-columns", "1fr 1fr")
                }
                gap(12.px)
                if (enabled) property("opacity", "1") else property("opacity", "0.55")
            }
        }) {
            ReasonBox(
                title = "Pourquoi activer",
                accent = AppColors.success,
                background = AppColors.successBg,
                reasons = activateReasons,
                positive = true,
            )
            ReasonBox(
                title = "Pourquoi désactiver",
                accent = AppColors.warning,
                background = AppColors.warningBg,
                reasons = deactivateReasons,
                positive = false,
            )
        }
    }
}

@Composable
private fun ReasonBox(
    title: String,
    accent: CSSColorValue,
    background: String,
    reasons: List<String>,
    positive: Boolean,
) {
    Div({
        style {
            backgroundColor(Color(background))
            borderRadius(10.px)
            padding(12.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                marginBottom(8.px)
                fontWeight("600")
                fontSize(13.px)
                color(accent)
            }
        }) {
            Text(if (positive) "✓" else "✕")
            Text(title)
        }
        reasons.forEach { reason ->
            P({
                style {
                    margin(0.px, 0.px, 4.px, 0.px)
                    fontSize(12.px)
                    color(AppColors.text)
                    lineHeight("1.4")
                }
            }) { Text("• $reason") }
        }
    }
}

@Composable
private fun CustomMessageSection(
    message: String,
    onMessageChange: (String) -> Unit,
) {
    Div({
        style {
            cardStyle()
            property("box-shadow", "none")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            Text("💬")
            H4({ style { margin(0.px); fontSize(15.px) } }) { Text("Message personnalisé (optionnel)") }
        }
        TextArea(value = message, attrs = {
            style {
                width(100.percent)
                minHeight(88.px)
                padding(12.px)
                borderRadius(8.px)
                border(1.px, LineStyle.Solid, AppColors.border)
                property("box-sizing", "border-box")
                property("resize", "vertical")
                fontSize(14.px)
                property("font-family", "inherit")
            }
            attr("placeholder", "Ajoutez un message personnalisé pour vos invités…")
            ref { element ->
                element.oninput = { event ->
                    onMessageChange((event.target as HTMLTextAreaElement).value)
                }
                onDispose { element.oninput = null }
            }
        })
        P({
            style {
                margin(8.px, 0.px, 0.px, 0.px)
                fontSize(12.px)
                color(AppColors.muted)
            }
        }) {
            Text("Ce message apparaîtra sur la page de vérification invité.")
        }
    }
}

@Composable
private fun ToggleSwitch(checked: Boolean, onChange: (Boolean) -> Unit) {
    Div(attrs = {
        style {
            width(48.px)
            height(28.px)
            borderRadius(999.px)
            backgroundColor(if (checked) AppColors.primary else Color("#d1d5db"))
            cursor("pointer")
            property("transition", "background-color 0.2s")
            property("position", "relative")
            property("flex-shrink", "0")
        }
        onClick { onChange(!checked) }
    }) {
        Div({
            style {
                width(22.px)
                height(22.px)
                borderRadius(50.percent)
                backgroundColor(AppColors.surface)
                property("position", "absolute")
                property("top", "3px")
                property("left", if (checked) "23px" else "3px")
                property("transition", "left 0.2s")
                property("box-shadow", "0 1px 3px rgba(0,0,0,0.2)")
            }
        })
    }
}
